import { AuthPermissions } from './AuthPermissions';
import { verifyAuthPermission } from './VerifyAuthPermission';

// Ordered list of candidate landing routes, mirroring the sidebar navigation
// order. Each entry is keyed on the same permission(s) that guard the route in
// components/app.tsx, so getDefaultRoute never hands back a route the user would
// immediately be bounced from. Routes are gated by their menu permission
// (/risk-indicators additionally honors SYSTEM_ADMIN, matching its route guard).
const DEFAULT_ROUTES: ReadonlyArray<{ permissions: AuthPermissions[]; path: string }> = [
  { permissions: [AuthPermissions.MENU_DASHBOARD], path: '/dashboard' },
  { permissions: [AuthPermissions.MENU_DEVICES], path: '/devices' },
  { permissions: [AuthPermissions.MENU_DEVICE_OWNERS], path: '/device-owners' },
  { permissions: [AuthPermissions.MENU_APPLICATIONS], path: '/applications' },
  { permissions: [AuthPermissions.MENU_REQUEST_ANALYSIS], path: '/request-analysis' },
  { permissions: [AuthPermissions.MENU_POLICIES], path: '/policies' },
  { permissions: [AuthPermissions.MENU_MANAGED_APPS], path: '/app-management' },
  { permissions: [AuthPermissions.MENU_RISK_INDICATORS, AuthPermissions.SYSTEM_ADMIN], path: '/risk-indicators' },
  { permissions: [AuthPermissions.MENU_CUSTOMER_SUPPORT], path: '/customer-support' },
];

/**
 * Returns the route the user should land on, following sidebar priority order
 * and skipping any route the user cannot access. Returns '' when the user has
 * access to none of them.
 *
 * Used both for the post-login default (components/index.tsx) and as the redirect
 * target when a user hits a route they lack permission for (the ProtectedRoute
 * fallback in components/app.tsx). Because each candidate is keyed on the same
 * permission that guards the route, callers can redirect to the result without
 * risking a bounce back to a guarded route (which would loop or render blank).
 */
export function getDefaultRoute(): string {
  const match = DEFAULT_ROUTES.find(({ permissions }) => permissions.some(p => verifyAuthPermission(p)));
  return match ? match.path : '';
}
